sn-hero-header-type-7 {
  .sn-hero-header-type-7 {
    background-color: var(--color-surface-darkest); // #000031
    color: var(--text-color-alternate);

    // Top padding trimmed to pull the hero content up to the legacy page's geometry.
    // On the original, h1 sits at ~210px = 114px fixed header + 96px hero padding-top,
    // with the breadcrumbs absolutely overlaid *inside* that padding (they take no flow
    // height). On ME the breadcrumbs are a separate 48px band above the hero, so matching
    // the original's h1 position means this padding must absorb both that band and the
    // hero's own extra: 48px lands h1 at ~209px at 1440 (was 120px → 281px).
    // padding-bottom is unchanged — it sits below the CTA and does not affect displacement.
    padding-top: px-to-rem(40px);
    padding-bottom: map-get($spacings, 3xl); // 40px

    @include desktop {
      padding-top: px-to-rem(44px);
    }

    @include above(960px) {
      padding-top: px-to-rem(48px);
      padding-bottom: map-get($spacings, 7xl); // 112px
    }
  }

  .sn-hero-header-type-7__container {
    max-width: $sn-container-max-width;
    margin: 0 auto;
    padding: 0 px-to-rem(16px);

    @include desktop {
      padding: 0 px-to-rem(24px);
    }
  }

  // ── Content column (single centered) ─────────────────────────────
  .sn-hero-header-type-7__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: px-to-rem(800px);
    margin: 0 auto;
  }

  // ── Heading ───────────────────────────────────────────────────────
  me-heading {
    color: var(--text-color-alternate);
    display: block;
    margin-bottom: px-to-rem(12px);

    @include desktop {
      // Matches the legacy page's post-heading rhythm (~26px); ME previously ran tighter (16px).
      margin-bottom: px-to-rem(24px);
    }

    .me-heading {
      font-size: map-get($font-sizes, 3xl);

      @include desktop {
        font-size: px-to-rem(52px);
      }
    }
  }

  // ── Description ───────────────────────────────────────────────────
  .sn-hero-header-type-7__description {
    margin-bottom: px-to-rem(24px);
    width: 100%;

    @include desktop {
      // Matches the legacy page's post-description rhythm (~36px). ME previously escalated to
      // 48px at ≥960, pushing the CTA down; aligning here also lifts it slightly.
      margin-bottom: px-to-rem(36px);
    }

    me-text {
      display: block;
      color: var(--text-color-alternate);
      font-size: px-to-rem(14px);
      line-height: px-to-rem(20px);

      @include above(960px) {
        font-size: px-to-rem(16px);
        line-height: px-to-rem(24px);
      }
    }
  }

  // ── Action (form) ─────────────────────────────────────────────────
  .sn-hero-header-type-7__action {
    width: 100%;

    form-free-trial-type-1 {
      display: block;
      width: 100%;
    }

    // Defensive: footer's global `.form-free-trial-type-1 { justify-content: flex-end }`
    // leaks across Light DOM; keep this form centered regardless.
    .form-free-trial-type-1 {
      justify-content: center;
    }

    .form-free-trial-type-1__row {
      justify-content: center;
    }

    @include below($breakpoint-desktop) {
      .form-free-trial-type-1__row {
        align-items: center; // horizontally centers cells in stacked (column) mode
      }

      // The input is a fixed 280px, but on mobile its cell (and the field wrapper) stretch
      // to full width — so the error message (a full-width, left-aligned block under the
      // input) detaches and slides to the section's left edge. Constrain the field wrapper
      // to the input width and center it, so the error stays directly under the input —
      // matching desktop, where the 280px cell already does this.
      .form-free-trial-type-1__input-wrapper {
        max-width: px-to-rem(280px);
        margin-inline: auto;
      }
    }

    @include desktop {
      .form-free-trial-type-1__row {
        flex-wrap: nowrap;

        // align-items: stretch (default) keeps button flush with input baseline
      }

      .form-free-trial-type-1__cell:not(.form-free-trial-type-1__cell--submit) {
        flex: 0 0 px-to-rem(280px);
      }

      // Match the legacy hero CTA width (171 → ~195px). Button height already matches,
      // so only the min-width is restored. Scoped here rather than in the shared
      // form-free-trial-type-1 so other pages using that form are unaffected.
      .form-free-trial-type-1__cell--submit .me-button {
        min-width: px-to-rem(195px);
      }
    }
  }

  // ── Awards / badges ───────────────────────────────────────────────
  .sn-hero-header-type-7__awards {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: map-get($spacings, lg);
    padding-top: px-to-rem(32px);
    width: 100%;

    @include desktop {
      flex-direction: row;
      align-items: center;
      justify-content: space-between; // heading left, badges right
      padding-top: px-to-rem(40px);
      gap: map-get($spacings, xl);
    }
  }

  .sn-hero-header-type-7__awards-heading {
    color: var(--text-color-alternate);
    font-weight: map-get($font-weights, bold);
    font-size: map-get($font-sizes, 2xl);
    line-height: map-get($line-heights, lg);
    margin: 0;
    text-align: center;
    max-width: px-to-rem(280px);

    @include desktop {
      flex-shrink: 0;
    }
  }

  .sn-hero-header-type-7__badges {
    display: flex;
    flex-wrap: wrap;
    gap: map-get($spacings, md);
    align-items: center;
    justify-content: center;
  }

  .sn-hero-header-type-7__badge-link {
    display: inline-flex;
    flex-shrink: 0;

    &:focus-visible {
      outline: 2px solid var(--text-color-alternate);
      outline-offset: 2px;
      border-radius: var(--radius-size-sm);
    }
  }

  .sn-hero-header-type-7__badge-img {
    width: px-to-rem(71px);
    height: auto;

    // G2 badges share a fixed 112:129 ratio — reserve the box via aspect-ratio so the
    // pre-load placeholder matches the rendered size exactly (no CLS), independent of
    // the intrinsic file dimensions. object-fit keeps any badge undistorted. Matches
    // the canonical badge pattern in sn-awards-type-1.
    aspect-ratio: 112 / 129;
    object-fit: contain;
    display: block;

    @include desktop {
      width: px-to-rem(96px);
    }
  }

  // ── Sign-link ─────────────────────────────────────────────────────
  .sn-hero-header-type-7__sign-link {
    color: var(--text-color-alternate);
    padding-top: px-to-rem(32px);
    text-align: center;

    @include desktop {
      padding-top: px-to-rem(24px);
      display: flex;
      flex-flow: row wrap;
      align-items: center;
      justify-content: center;
      column-gap: px-to-rem(8px);
    }

    @include above(960px) {
      padding-top: px-to-rem(32px);
    }

    @include above(1200px) {
      padding-top: px-to-rem(40px);
    }

    &:empty {
      display: none;
    }

    me-text {
      display: block;

      @include desktop {
        p {
          margin-bottom: 0;
        }
      }
    }

    me-button,
    .me-button {
      color: var(--text-color-alternate);

      .me-button__icon {
        color: var(--text-color-alternate);
      }
    }

    // Ghost button underline: white on dark background.
    .me-button--ghost {
      &::before {
        background-color: var(--text-color-alternate);
      }
    }

    // Keep underline width equal to text width only (excluding icon + gap).
    .me-button--ghost.me-button--icon-after {
      &::before {
        right: calc(#{map-get($spacings, xl)} + #{map-get($spacings, sm)});
      }

      &.is-hovered::before,
      &:hover::before {
        width: calc(100% - (#{map-get($spacings, xl)} + #{map-get($spacings, sm)}));
      }
    }
  }
}
